import React from "react";
import debug from "src/modules/debug-logger";
import { compose } from "recompose";
import { connect } from "react-redux";
import environment from "src/utils/environment";
import { RPCRequests } from "src/modules/communication-protobuf";
import { withParentStation } from "src/modules/parent-station/ParentStation/Context";
import { getUserId } from "src/modules/auth/duck/selectors";
import * as cloud from "src/modules/activity-log/ajax/cloud";
import decodeOgg from "src/browser/decodeOgg";
import getCachedResponse from "./getCachedResponse";
import AudioReplayFailureDialog from "./AudioReplayFailureDialog";
import cacheResponse from "./cacheResponse";

const { isBrowser } = environment;

export default function withEventAudioPlayer(Component) {
  class EventAudioPlayer extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        status: null,
        fileName: null,
        play: this.play,
        stop: this.stop,
        seek: this.seek,
        seekCommitted: this.seekCommitted,
        getCurrentTime: this.getCurrentTime,
        getDuration: this.getDuration
      };
      this.audio = new Audio();
    }
    fetchAttempt = 1;

    componentWillUnmount() {
      this.audio.removeEventListener("ended", this.handleAudioEndedEvent);
      this.audio.onerror = null;
    }

    async getSignedUrlFromBabyStation(fileName) {
      const { parentStation } = this.props;
      if (parentStation == null || parentStation.rpc == null) {
        throw new Error("Missing ParentStation RPC instance, cannot proceed.");
      }
      const { rpc, babyStation } = parentStation;
      const request = RPCRequests.GetSignedUrl.create(fileName);
      const { signedUrl } = await rpc.call(request, babyStation.jid);
      return signedUrl;
    }

    async getSignedUrl(fileName) {
      const { userId } = this.props;
      const fileOwnerId = fileName.split("_")[0];
      if (userId === fileOwnerId) {
        return await cloud.getSignedUrl(fileName);
      } else {
        return await this.getSignedUrlFromBabyStation(fileName);
      }
    }

    handleAudioEndedEvent = () =>
      this.setState({ status: null, fileName: null });

    async getAndCacheResponse(fileName) {
      const signedUrl = await this.getSignedUrl(fileName);
      const response = await fetch(signedUrl);
      debug.activityLog("Fetch sound response", response);
      if (response.ok) {
        cacheResponse(response.clone());
        return response;
      } else {
        throw new Error("Response not ok, code: " + response.code);
      }
    }

    tryPlayDecoded = async (fileName) => {
      if (!isBrowser) return;
      this.setState({ status: "loading" });
      const response = await getCachedResponse(fileName);
      const arrayBuffer = await response.arrayBuffer();
      decodeOgg(this.audio, arrayBuffer, () =>
        this.setState({ status: "playing" })
      );
    };

    play = async (fileName) => {
      if (fileName == null) return;
      const previousFileName = this.state.fileName;
      if (previousFileName === fileName) {
        this.fetchAttempt++;
      } else this.fetchAttempt = 1;
      this.audio.pause();
      this.setState({
        status: "loading",
        fileName
      });

      let response = await getCachedResponse(fileName);

      if (response == null) {
        try {
          response = await this.getAndCacheResponse(fileName);
        } catch (error) {
          debug.activityLog(
            `Fetch attempt number ${this.fetchAttempt} failed`,
            error
          );
          if (this.fetchAttempt === 1) {
            this.play(fileName);
          } else {
            this.setState({ status: "error", fileName: null });
          }
          return;
        }
      }

      const blob = await response.blob();
      if (this.state.fileName === fileName) {
        this.audio.src = URL.createObjectURL(blob);
        this.audio.addEventListener("ended", this.handleAudioEndedEvent, {
          once: true
        });
        this.audio.onerror = () => this.tryPlayDecoded(fileName);
        this.audio.play();
        this.setState({ status: "playing" });
      }
    };

    stop = () => {
      this.audio.pause();
      this.setState({ status: null, fileName: null });
    };

    seek = (time) => {
      if (!this.audio.paused) this.audio.pause();
      this.audio.currentTime = time;
    };
    seekCommitted = (time) => {
      this.audio.currentTime = time;
      this.audio.play();
    };

    getDuration = () => {
      return this.audio.duration;
    };
    getCurrentTime = () => {
      return this.audio.currentTime;
    };

    closeDialog = () => this.setState({ status: null });

    render() {
      const { status } = this.state;
      return (
        <>
          <AudioReplayFailureDialog
            onDismiss={this.closeDialog}
            isOpen={status === "error"}
          />
          <Component eventAudioPlayer={this.state} {...this.props} />
        </>
      );
    }
  }

  function mapStateToProps(state) {
    return {
      userId: getUserId(state)
    };
  }

  const enhance = compose(connect(mapStateToProps), withParentStation);

  return enhance(EventAudioPlayer);
}
